/**
 * Shared Meta placements catalog for AdGroupPlacementsMetaForm and CampaignLogs.
 * Single source of truth for placement keys and labels.
 */

import {
  META_ADGROUP_PLACEMENTS_TYPE_ADVANTAGE,
  META_ADGROUP_PLACEMENTS_TYPE_MANUAL,
} from "../../i18n/keysTranslations";

export const PLACEMENT_GROUPS = [
  {
    groupKey: "feeds",
    groupLabel: "Feeds",
    placements: [
      { key: "facebookFeed", label: "Facebook Feed", platform: "facebook" },
      {
        key: "facebookProfileFeed",
        label: "Facebook Profile Feed",
        platform: "facebook",
      },
      { key: "instagramFeed", label: "Instagram Feed", platform: "instagram" },
      {
        key: "instagramProfileFeed",
        label: "Instagram Profile Feed",
        platform: "instagram",
      },
      {
        key: "facebookMarketplace",
        label: "Facebook Marketplace",
        platform: "facebook",
      },
      {
        key: "facebookRightColumn",
        label: "Facebook Right Column",
        platform: "facebook",
      },
      {
        key: "instagramExplore",
        label: "Instagram Explore",
        platform: "instagram",
      },
      {
        key: "instagramExploreHome",
        label: "Instagram Explore Home",
        platform: "instagram",
      },
      {
        key: "messengerInbox",
        label: "Messenger Inbox",
        platform: "messenger",
      },
      {
        key: "facebookNotifications",
        label: "Facebook Notifications",
        platform: "facebook",
      },
    ],
  },
  {
    groupKey: "storiesAndReels",
    groupLabel: "Stories and Reels",
    placements: [
      {
        key: "instagramStories",
        label: "Instagram Stories",
        platform: "instagram",
      },
      {
        key: "facebookStories",
        label: "Facebook Stories",
        platform: "facebook",
      },
      {
        key: "messengerStories",
        label: "Messenger Stories",
        platform: "messenger",
      },
      {
        key: "instagramReels",
        label: "Instagram Reels",
        platform: "instagram",
      },
      {
        key: "instagramProfileReels",
        label: "Instagram Profile Reels",
        platform: "instagram",
      },
      {
        key: "facebookReels",
        label: "Facebook Reels",
        platform: "facebook",
      },
    ],
  },
  {
    groupKey: "inStreamAds",
    groupLabel: "In-stream ads for videos and reels",
    placements: [
      {
        key: "facebookInstreamVideos",
        label: "Facebook In-Stream Videos",
        platform: "facebook",
      },
      {
        key: "adsOnFacebookReels",
        label: "Ads on Facebook Reels",
        platform: "facebook",
      },
    ],
  },
  {
    groupKey: "searchResults",
    groupLabel: "Search results",
    placements: [
      {
        key: "facebookSearchResults",
        label: "Facebook Search Results",
        platform: "facebook",
      },
      {
        key: "instagramSearchResults",
        label: "Instagram Search Results",
        platform: "instagram",
      },
    ],
  },
  {
    groupKey: "appsAndSites",
    groupLabel: "Apps and sites",
    placements: [
      {
        key: "audienceNetworkNativeBannerInterstitial",
        label: "Audience Network Native, Banner and Interstitial",
        platform: "audienceNetwork",
      },
      {
        key: "audienceNetworkRewardedVideos",
        label: "Audience Network Rewarded Videos",
        platform: "audienceNetwork",
      },
    ],
  },
];

export const PLATFORMS = [
  { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" },
  { key: "messenger", label: "Messenger" },
  { key: "audienceNetwork", label: "Audience Network" },
];

const PLACEMENT_KEY_TO_LABEL = new Map();
PLACEMENT_GROUPS.forEach((group) => {
  group.placements.forEach((p) => PLACEMENT_KEY_TO_LABEL.set(p.key, p.label));
});

/**
 * @param {string} key - Placement key (e.g. facebookFeed)
 * @returns {string} Human label or key if not found
 */
export const getPlacementLabel = (key) =>
  PLACEMENT_KEY_TO_LABEL.get(key) || key;

/**
 * Format placements object for logs: grouped by platform with human labels.
 * @param {Object} value - { type, facebookPositions?, instagramPositions?, messengerPositions?, audienceNetworkPositions? }
 * @param {(key: string) => string} t - i18n function for type labels
 * @returns {string}
 */
export const formatPlacementsForLog = (value, t) => {
  if (!value || typeof value !== "object") return "";
  const typeKey =
    value.type === "advantage"
      ? META_ADGROUP_PLACEMENTS_TYPE_ADVANTAGE
      : META_ADGROUP_PLACEMENTS_TYPE_MANUAL;
  const typeLabel = t ? t(typeKey) : value.type;
  if (value.type === "advantage") return typeLabel;

  const byPlatform = {
    facebook: (value.facebookPositions || []).map(getPlacementLabel),
    instagram: (value.instagramPositions || []).map(getPlacementLabel),
    messenger: (value.messengerPositions || []).map(getPlacementLabel),
    audienceNetwork: (value.audienceNetworkPositions || []).map(
      getPlacementLabel
    ),
  };
  const platformLabels = PLATFORMS.map((p) => p.label);
  const parts = [];
  PLATFORMS.forEach((platform, i) => {
    const labels = byPlatform[platform.key];
    if (labels?.length > 0) {
      parts.push(`${platformLabels[i]}: ${labels.join(", ")}`);
    }
  });
  return parts.length > 0 ? `${typeLabel}: ${parts.join(" | ")}` : typeLabel;
};
